import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import MicOffOutlinedIcon from '@mui/icons-material/MicOffOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import SensorsIcon from '@mui/icons-material/Sensors';
import {
  API_BASE_URL, ROUTE, CLIENT_ID, WS_BASE_URL,
  SEGMENT_LENGTH_FALLBACK, HEARTBEAT_MS, ROLE,
} from './StreamConfig';
import {
  COLORS, FONT_FAMILY, FONT_HEADING, appTheme, BUTTON_HOVER, BUTTON_HOVER_SX,
} from '../../talkTreeShared/Tree/constants/theme';

const BLUE = COLORS.primary;
const GREEN = COLORS.success;
const CORAL = '#E5634D';
const CORAL_BG = '#F8E7E4';
const STOPPED_GRAY = '#92918D';
const INFO_BLUE = '#4C8EEC';
const PAGE_BG = '#F9F7F6';
const BAR_COUNT = 7;
const MIN_SEGMENT_BYTES = 2048;
const JOIN_CODE_LENGTH = 6;

const PHASE = {
  IDLE: 'idle',
  REQUESTING: 'requesting',
  PREPARING: 'preparing',
  WAITING: 'waiting',
  DENIED: 'denied',
  ERROR: 'error',
  RECORDING: 'recording',
  PAUSED: 'paused',
  STOPPED_LOCAL: 'stopped_local',
  ENDED: 'ended',
};

const formatMicError = (err) => {
  switch (err && err.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Microphone access was blocked. Click “Try again” below to allow it in your browser settings then tap “Start streaming” again.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone was found. Connect one and try again.';
    case 'NotReadableError':
      return 'Your microphone is in use by another app. Close it and try again.';
    default:
      return 'Could not access the microphone. Check your settings and try again.';
  }
};

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
    .find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

// Key used to persist aux device connection info in sessionStorage across page reloads.
const auxSessionKey = (token) => `aux_session_${token}`;

const getTokenFromUrl = () => {
  const hash = window.location.hash || '';
  const qIndex = hash.indexOf('?');
  if (qIndex !== -1) {
    const p = new URLSearchParams(hash.slice(qIndex + 1));
    if (p.get('token')) return p.get('token');
  }
  return new URLSearchParams(window.location.search).get('token') || '';
};


const markUrlStopped = () => {
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('stopped', '1');
    window.history.replaceState(null, '', u.toString());
  } catch { /* ignore */ }
};
const isUrlMarkedStopped = () => {
  try {
    return new URL(window.location.href).searchParams.get('stopped') === '1';
  } catch {
    return false;
  }
};
const clearUrlStoppedMarker = () => {
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete('stopped');
    window.history.replaceState(null, '', u.toString());
  } catch { /* ignore */ }
};

const formatDeviceLabel = (id) => {
  if (!id) return '';
  const m = String(id).match(/(\d+)$/);
  return m ? `Device ${m[1].padStart(2, '0')}` : String(id);
};

function StatusPill({ bg, icon, label }) {
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 1,
      backgroundColor: bg, color: '#fff', borderRadius: '999px',
      px: 2.5, py: 1.1, mt: 3, fontSize: '18px', fontWeight: 700, lineHeight: 1,
    }}>
      {icon}
      {label}
    </Box>
  );
}

function NoticeBox({ icon, color, bg, children }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2, textAlign: 'left',
      backgroundColor: bg, color, borderRadius: '12px',
      px: 2.5, py: 2, mt: 3, fontSize: '18px', lineHeight: 1.5,
    }}>
      <Box sx={{ display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Box>{children}</Box>
    </Box>
  );
}

const AuxRecord = ({ token: tokenProp }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [token, setTokenState] = useState(tokenProp || getTokenFromUrl());
  const tokenRef = useRef(token);
  const setToken = useCallback((t) => {
    tokenRef.current = t;
    setTokenState(t);
  }, []);

  const [phase, setPhase] = useState(PHASE.IDLE);
  const [error, setError] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [sessionTitle, setSessionTitle] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [bars, setBars] = useState(() => Array(BAR_COUNT).fill(0.08));
  const [segmentsSent, setSegmentsSent] = useState(0);
  const [wsConnected, setWsConnected] = useState(true);
  const [joinCodeInput, setJoinCodeInput] = useState('');
  const [joinSubmitting, setJoinSubmitting] = useState(false);
  const [joinError, setJoinError] = useState('');
  // 'idle' | 'active' | 'unsupported' | 'blocked'
  const [wakeLockStatus, setWakeLockStatus] = useState('idle');

  const credentialRef = useRef(null);
  const segmentLenRef = useRef(SEGMENT_LENGTH_FALLBACK);
  const serverAnchorMsRef = useRef(0);
  const joinPerfRef = useRef(0);
  const segmentIndexRef = useRef(0);
  const segStartMsRef = useRef(0);
  const deviceIdRef = useRef('');
  const uploadsRef = useRef([]);
  const syncSentPerfRef = useRef(0);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const lastVisualRef = useRef(0);
  const timerRef = useRef(null);
  const elapsedRef = useRef(0);

  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectRef = useRef(null);
  const closedRef = useRef(false);
  const finalizeRef = useRef(null);
  const auxStopResolveRef = useRef(null);
  const wakeLockRef = useRef(null);

  // Mixpanel tracking refs
  const hasStartedRecordingOnceRef = useRef(false);
  const segmentsSentRef = useRef(0); // ref so socket-callback closures never report a stale count

  const nowMs = useCallback(
    () => Math.round(serverAnchorMsRef.current + (performance.now() - joinPerfRef.current)),
    [],
  );

  const stopTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
  }, []);

  const cleanupMedia = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') audioCtxRef.current.close().catch(() => {});
    audioCtxRef.current = null;
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  }, []);

  // ---- Screen Wake Lock (keeps the screen on while the device is capturing) ----
  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) {
      console.warn('Wake lock: API not available (needs HTTPS / supported browser)');
      setWakeLockStatus('unsupported');
      return;
    }
    if (wakeLockRef.current) return;                    // already held
    if (document.visibilityState !== 'visible') return; // API requires a visible page
    try {
      const sentinel = await navigator.wakeLock.request('screen');
      wakeLockRef.current = sentinel;
      setWakeLockStatus('active');
      console.log('✓ wake lock acquired');
      sentinel.addEventListener('release', () => {
        // Fires on tab hide, battery saver, or policy override.
        console.log('wake lock released');
        if (wakeLockRef.current === sentinel) wakeLockRef.current = null;
        setWakeLockStatus('idle');
      });
    } catch (e) {
      // NotAllowedError: policy, battery saver, or permissions-policy
      console.warn('Wake lock unavailable:', e?.name || e);
      wakeLockRef.current = null;
      setWakeLockStatus('blocked');
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    const s = wakeLockRef.current;
    wakeLockRef.current = null;
    if (s) s.release().catch(() => {});
    setWakeLockStatus('idle');
  }, []);

  const startVisualizer = useCallback((stream) => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    ctx.createMediaStreamSource(stream).connect(analyser);
    audioCtxRef.current = ctx;
    const data = new Uint8Array(analyser.frequencyBinCount);
    const seg = Math.floor(data.length / BAR_COUNT) || 1;
    const tick = (ts) => {
      rafRef.current = requestAnimationFrame(tick);
      if (ts - lastVisualRef.current < 50) return;
      lastVisualRef.current = ts;
      analyser.getByteFrequencyData(data);
      const next = [];
      for (let i = 0; i < BAR_COUNT; i += 1) {
        let sum = 0;
        for (let j = 0; j < seg; j += 1) sum += data[i * seg + j];
        next.push(Math.max(0.08, sum / seg / 255));
      }
      setBars(next);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const freezeVisualizer = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setBars(Array(BAR_COUNT).fill(0.08));
  }, []);

  const callPrepare = useCallback(async (body) => {
    const url = `${API_BASE_URL}/${ROUTE.PREPARE_AUX}`;
    const isCodeJoin = !!body.joinCode;
    console.log('→ prepareAux POST', url, '| body:', body);
    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (e) {
      console.error('✗ prepare fetch threw (network/CORS):', e);
      throw new Error('Could not reach the server. Check your connection and try again.');
    }
    console.log('← prepare status', res.status);
    if (res.status === 404) throw new Error(isCodeJoin ? "That code isn't valid." : 'This session no longer exists.');
    if (res.status === 409) throw new Error('This session has already ended.');
    if (!res.ok) {
      throw new Error(isCodeJoin
        ? `Could not join with that code (HTTP ${res.status}).`
        : `Could not join the session (HTTP ${res.status}).`);
    }
    return res.json();
  }, []);

  const uploadSegment = useCallback(async (index, startMs, endMs, blob) => {
    const cred = credentialRef.current;
    if (!cred) { console.warn('uploadSegment: no credential'); return; }
    const form = new FormData();
    Object.entries(cred.fields).forEach(([k, v]) => form.append(k, v));
    const key = `${CLIENT_ID}/${tokenRef.current}/${deviceIdRef.current}/seg_${String(index).padStart(3, '0')}_${startMs}_${endMs}`;
    form.set('key', key);
    form.set('x-amz-meta-segment-index', String(index));
    form.set('x-amz-meta-start-ms', String(startMs));
    form.set('x-amz-meta-end-ms', String(endMs));
    form.append('Content-Type', blob.type || 'audio/webm');
    form.append('file', blob);
    const res = await fetch(cred.url, { method: 'POST', body: form });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      console.error('✗ segment upload failed', res.status, body);
      throw new Error(`segment upload failed: ${res.status}`);
    }
    console.log('✓ segment uploaded', index);
  }, []);

  const makeOnDataAvailable = useCallback(() => (e) => {
    if (!e.data || e.data.size < MIN_SEGMENT_BYTES) {
      console.warn('Skipping empty recorder artifact:', e.data?.size || 0, 'bytes');
      return;
    }
    const index = segmentIndexRef.current;
    segmentIndexRef.current += 1;
    try {
      const stored = sessionStorage.getItem(auxSessionKey(tokenRef.current));
      if (stored) {
        sessionStorage.setItem(auxSessionKey(tokenRef.current), JSON.stringify({ ...JSON.parse(stored), segmentIndex: segmentIndexRef.current }));
      }
    } catch { /* ignore */ }
    const startMs = segStartMsRef.current;
    const endMs = nowMs();
    segStartMsRef.current = endMs;
    const p = uploadSegment(index, startMs, endMs, e.data)
      .then(() => {
        setSegmentsSent((c) => c + 1);
        segmentsSentRef.current += 1;
        uploadsRef.current = uploadsRef.current.filter((u) => u !== p);
      })
      .catch((uErr) => {
        console.error(uErr);
        uploadsRef.current = uploadsRef.current.filter((u) => u !== p);
      });
    uploadsRef.current.push(p);
  }, [nowMs, uploadSegment]);

  const sendWs = useCallback((msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      console.log('→ WS send (aux)', msg.type, msg.state || '');
    } else {
      console.warn('⚠ WS send DROPPED (aux, socket not open):', msg.type, '| readyState:', ws ? ws.readyState : 'no socket');
    }
  }, []);

  const requestAuxStop = useCallback(() => new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      auxStopResolveRef.current = null;
      resolve();
    };
    auxStopResolveRef.current = finish;
    sendWs({ type: 'AUX_STOP' });
    // Do not trap the user indefinitely if the network disappears during stop.
    setTimeout(finish, 3000);
  }), [sendWs]);

  const closeSocket = useCallback(() => {
    closedRef.current = true;
    if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    const ws = wsRef.current;
    if (ws) {
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close();
    }
    wsRef.current = null;
  }, []);

  const handleSessionEnded = useCallback(() => {
    if (finalizeRef.current) return;
    mixpanel.track('auxSessionEnded', {
      token: tokenRef.current,
      deviceId: deviceIdRef.current,
      elapsedSeconds: elapsedRef.current,
      segmentsSent: segmentsSentRef.current,
    });
    stopTimers();
    freezeVisualizer();

    const finishEnd = () => {
      cleanupMedia();
      recorderRef.current = null;
      sendWs({ type: 'STATE_UPDATE', state: 'stopped' });
      closeSocket();
      try { sessionStorage.removeItem(auxSessionKey(tokenRef.current)); } catch { /* ignore */ }
      setPhase(PHASE.ENDED);
    };

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      finalizeRef.current = new Promise((resolve) => {
        recorder.onstop = async () => {
          try {
            await Promise.allSettled(uploadsRef.current);
          } catch (e) {}
          finishEnd();
          resolve();
        };
      });
      recorder.stop();
    } else {
      finishEnd();
    }
  }, [stopTimers, freezeVisualizer, sendWs, closeSocket, cleanupMedia]);

  const beginRecording = useCallback(async (startPaused = false) => {
    const existingRecorder = recorderRef.current;
    if (existingRecorder && existingRecorder.state !== 'inactive') return;

    setError('');
    setSegmentsSent(0);
    segmentsSentRef.current = 0;
    uploadsRef.current = [];

    let stream = streamRef.current;
    if (!stream) {
      if (!navigator.mediaDevices?.getUserMedia) {
        setPhase(PHASE.DENIED);
        setError('Recording is not supported in this browser, or the page is not on HTTPS.');
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
      } catch (err) {
        cleanupMedia();
        setPhase(PHASE.DENIED);
        setError(formatMicError(err));
        mixpanel.track('auxMicrophonePermissionDenied', { token: tokenRef.current, errorName: err?.name || 'unknown', context: 'session_start' });
        return;
      }
    }

    const mimeType = pickMimeType();
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    segStartMsRef.current = nowMs();
    recorder.ondataavailable = makeOnDataAvailable();
    recorder.start(segmentLenRef.current * 1000);
    recorderRef.current = recorder;

    if (!hasStartedRecordingOnceRef.current) {
      hasStartedRecordingOnceRef.current = true;
      mixpanel.track('auxRecordingStarted', { token: tokenRef.current, deviceId: deviceIdRef.current, startedPaused: startPaused });
    }

    elapsedRef.current = 0;
    setElapsed(0);
    if (startPaused) {
      recorder.pause();
      freezeVisualizer();
      setPhase(PHASE.PAUSED);
      sendWs({ type: 'STATE_UPDATE', state: 'paused' });
    } else {
      startVisualizer(stream);
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(elapsedRef.current);
      }, 1000);
      setPhase(PHASE.RECORDING);
      sendWs({ type: 'STATE_UPDATE', state: 'recording' });
    }
  }, [cleanupMedia, nowMs, makeOnDataAvailable, startVisualizer, freezeVisualizer, sendWs]);


  const pause = useCallback(() => {
    if (!recorderRef.current) return;
    mixpanel.track('auxRecordingPaused', { token: tokenRef.current, deviceId: deviceIdRef.current, elapsedSeconds: elapsedRef.current });
    const recorder = recorderRef.current;
    if (recorder?.state === 'recording') {
      recorder.requestData();
      recorder.pause();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    freezeVisualizer();
    setPhase(PHASE.PAUSED);
    sendWs({ type: 'STATE_UPDATE', state: 'paused' });
  }, [freezeVisualizer, sendWs]);

  const resume = useCallback(() => {
    if (!recorderRef.current) return; // nothing to resume — ignore
    mixpanel.track('auxRecordingResumed', { token: tokenRef.current, deviceId: deviceIdRef.current, elapsedSeconds: elapsedRef.current });
    const recorder = recorderRef.current;
    if (recorder?.state === 'paused') recorder.resume();
    segStartMsRef.current = nowMs();
    if (streamRef.current) startVisualizer(streamRef.current);
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
    }, 1000);
    setPhase(PHASE.RECORDING);
    sendWs({ type: 'STATE_UPDATE', state: 'recording' });
  }, [nowMs, startVisualizer, sendWs]);


  const stopLocalDevice = useCallback(() => {
    const confirmed = window.confirm(
      'Stop recording on this device? You will need to scan the QR code (or enter the join code) again to rejoin.'
    );
    if (!confirmed) return;

    mixpanel.track('auxStopRecordingClicked', { token: tokenRef.current, deviceId: deviceIdRef.current, elapsedSeconds: elapsedRef.current, segmentsSent: segmentsSentRef.current });

    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    freezeVisualizer();

    const finish = async () => {
      try {
        await Promise.allSettled(uploadsRef.current);
      } catch (e) {}
      cleanupMedia();
      recorderRef.current = null;
      await requestAuxStop();
      try {
        const stored = sessionStorage.getItem(auxSessionKey(tokenRef.current));
        sessionStorage.setItem(auxSessionKey(tokenRef.current), JSON.stringify({
          ...(stored ? JSON.parse(stored) : {}),
          stoppedLocally: true,
        }));
      } catch { /* ignore */ }
      markUrlStopped();
      closeSocket();
      setPhase(PHASE.STOPPED_LOCAL);
    };

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = () => { finish(); };
      recorder.stop();
    } else {
      finish();
    }
  }, [freezeVisualizer, cleanupMedia, requestAuxStop, closeSocket]);

  const openSocket = useCallback((devId, tokOverride) => {
    const tok = tokOverride || tokenRef.current;
    if (!tok) {
      console.warn('openSocket skipped: no token (prepare must succeed first)');
      return;
    }
    if (!WS_BASE_URL || WS_BASE_URL.includes('<')) {
      console.error('openSocket: WS_BASE_URL missing or placeholder:', WS_BASE_URL);
      return;
    }
    const qs = new URLSearchParams({ token: tok, deviceId: devId, role: ROLE.AUX });
    const wsBase = WS_BASE_URL.replace(/\/+$/, '');
    const wsUrl = `${wsBase}?${qs}`;
    console.log('→ WS connect (aux)', wsUrl);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onopen = () => {
      setWsConnected(true);
      if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
      heartbeatRef.current = setInterval(() => sendWs({ type: 'HEARTBEAT' }), HEARTBEAT_MS);
      syncSentPerfRef.current = performance.now();
      sendWs({ type: 'SYNC' });
    };
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        console.log('← WS recv (aux)', msg.type, msg);
        if (typeof msg.serverTime === 'number') {
          const rttMs = performance.now() - syncSentPerfRef.current;
          serverAnchorMsRef.current = msg.serverTime * 1000 + rttMs / 2;
          joinPerfRef.current = performance.now();
        }

        if (msg.type === 'SESSION_ENDED') handleSessionEnded();
        if (msg.type === 'SESSION_START') beginRecording();
        if (msg.type === 'SESSION_PAUSE') {
          if (recorderRef.current && recorderRef.current.state !== 'inactive') pause();
          else beginRecording(true);
        }
        if (msg.type === 'SESSION_RESUME') resume();
        if (msg.type === 'AUX_STOP_ACK') auxStopResolveRef.current?.();
        if (msg.type === 'SESSION_STATE') { }
      } catch (e) {}
    };
    ws.onclose = () => {
      setWsConnected(false);
      if (!closedRef.current) reconnectRef.current = setTimeout(() => {
        openSocket(devId);
      }, 2000);
    };
    ws.onerror = () => ws.close();
  }, [handleSessionEnded, sendWs, beginRecording, pause, resume]);

  // Shared post-prepare logic for both the QR flow and the join-code flow.
  const finishPrepareAndConnect = useCallback(async (prep, resolvedToken, perfAfterPrepare, serverNowMs) => {
    credentialRef.current = prep.uploadCredential;
    segmentLenRef.current = Number(prep.segmentLength) > 0 ? Number(prep.segmentLength) : SEGMENT_LENGTH_FALLBACK;
    serverAnchorMsRef.current = serverNowMs;
    joinPerfRef.current = perfAfterPrepare;
    segmentIndexRef.current = 0;
    deviceIdRef.current = prep.deviceId || '';
    setDeviceId(prep.deviceId);
    setSessionTitle(prep.sessionTitle || '');
    // Persist connection info so we can reconnect to the same device on page reload.
    try {
      sessionStorage.setItem(auxSessionKey(resolvedToken), JSON.stringify({
        deviceId: prep.deviceId,
        uploadCredential: prep.uploadCredential,
        segmentLength: prep.segmentLength,
        sessionTitle: prep.sessionTitle || '',
        serverTime: prep.serverTime,
        storedAt: Date.now(),
        segmentIndex: 0,
      }));
    } catch { /* ignore storage errors in restricted browser contexts */ }

    // Ask for mic permission NOW, while we have the student's join action as
    // the user gesture, rather than waiting for SESSION_START to arrive over
    // the socket later — a message handler firing getUserMedia has no
    // guaranteed user gesture behind it and can be silently blocked on some
    // mobile browsers. Getting it now means SESSION_START just has to start
    // an already-permitted recorder, which always works.
    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase(PHASE.DENIED);
      setError('Recording is not supported in this browser, or the page is not on HTTPS.');
      return;
    }
    setPhase(PHASE.REQUESTING);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
    } catch (err) {
      setPhase(PHASE.DENIED);
      setError(formatMicError(err));
      mixpanel.track('auxMicrophonePermissionDenied', { token: resolvedToken, errorName: err?.name || 'unknown', context: 'join' });
      return;
    }

    closedRef.current = false;
    openSocket(prep.deviceId, resolvedToken);
    setPhase(PHASE.WAITING);
    mixpanel.track('auxDeviceConnected', { token: resolvedToken, deviceId: prep.deviceId, sessionTitle: prep.sessionTitle || '' });
  }, [openSocket]);

  // QR flow: the token comes from the URL.
  const connect = useCallback(async () => {
    setError('');
    setPhase(PHASE.PREPARING);
    mixpanel.track('auxJoinAttempted', { token: tokenRef.current });
    let prep;
    const perfBeforePrepare = performance.now();
    try {
      prep = await callPrepare({ token: tokenRef.current, role: ROLE.AUX });
    } catch (err) {
      setPhase(PHASE.ERROR);
      const message = err.message || 'Could not join the session.';
      setError(message);
      mixpanel.track('auxJoinFailed', { token: tokenRef.current, errorMessage: message });
      return;
    }
    const perfAfterPrepare = performance.now();
    const rttMs = perfAfterPrepare - perfBeforePrepare;
    const serverNowMs = (prep.serverTime || Math.floor(Date.now() / 1000)) * 1000 + rttMs / 2;
    await finishPrepareAndConnect(prep, tokenRef.current, perfAfterPrepare, serverNowMs);
  }, [callPrepare, finishPrepareAndConnect]);

  // Join-code flow: no WebSocket opens until the server validates the code
  // and returns the session ID, which then becomes the token.
  const handleJoinWithCode = useCallback(async () => {
    const code = joinCodeInput.trim().toUpperCase();
    if (code.length !== JOIN_CODE_LENGTH) {
      setJoinError(`Enter the ${JOIN_CODE_LENGTH}-character code shown on the teacher's screen.`);
      return;
    }
    setJoinError('');
    setJoinSubmitting(true);
    mixpanel.track('auxJoinAttempted', { method: 'joinCode' });

    let prep;
    const perfBeforePrepare = performance.now();
    try {
      prep = await callPrepare({ joinCode: code, role: ROLE.AUX });
    } catch (err) {
      setJoinSubmitting(false);
      const message = err.message || "That code isn't valid.";
      setJoinError(message);
      mixpanel.track('auxJoinFailed', { method: 'joinCode', errorMessage: message });
      return;
    }
    const perfAfterPrepare = performance.now();
    const rttMs = perfAfterPrepare - perfBeforePrepare;
    const serverNowMs = (prep.serverTime || Math.floor(Date.now() / 1000)) * 1000 + rttMs / 2;

    const resolvedToken = prep.sessionId || prep.token || '';
    if (!resolvedToken) {
      setJoinSubmitting(false);
      setJoinError("That code isn't valid.");
      mixpanel.track('auxJoinFailed', { method: 'joinCode', errorMessage: "That code isn't valid." });
      return;
    }

    setToken(resolvedToken);
    setJoinSubmitting(false);
    await finishPrepareAndConnect(prep, resolvedToken, perfAfterPrepare, serverNowMs);
  }, [joinCodeInput, callPrepare, finishPrepareAndConnect, setToken]);

  // Restores connection state from sessionStorage and rejoins the session
  // without calling callPrepare(), so a page reload doesn't register a new
  // device. Whether this device should be recording/paused/waiting is
  // resolved entirely by the backend's reply to SYNC (sent in openSocket's
  // onopen) — this function does not guess.
  const reconnectFromStorage = useCallback(async (stored) => {
    mixpanel.track('auxReconnectedFromStorage', { token: tokenRef.current, deviceId: stored.deviceId, segmentIndexAtReconnect: stored.segmentIndex || 0 });
    credentialRef.current = stored.uploadCredential;
    segmentLenRef.current = Number(stored.segmentLength) > 0 ? Number(stored.segmentLength) : SEGMENT_LENGTH_FALLBACK;
    serverAnchorMsRef.current = Date.now(); // best-effort; re-anchored properly if SYNC's reply carries serverTime
    joinPerfRef.current = performance.now();
    segmentIndexRef.current = stored.segmentIndex || 0; // resume numbering, not restart at 0
    deviceIdRef.current = stored.deviceId;
    setDeviceId(stored.deviceId);
    setSessionTitle(stored.sessionTitle || '');

    if (!navigator.mediaDevices?.getUserMedia) {
      setPhase(PHASE.DENIED);
      setError('Recording is not supported in this browser, or the page is not on HTTPS.');
      return;
    }
    setPhase(PHASE.REQUESTING);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
    } catch (err) {
      setPhase(PHASE.DENIED);
      setError(formatMicError(err));
      mixpanel.track('auxMicrophonePermissionDenied', { token: tokenRef.current, errorName: err?.name || 'unknown', context: 'reconnect' });
      return;
    }

    closedRef.current = false;
    openSocket(stored.deviceId);
    setPhase(PHASE.WAITING);
  }, [openSocket]);

  useEffect(() => () => {
    closedRef.current = true;
    stopTimers();
    cleanupMedia();
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
    const ws = wsRef.current;
    if (ws) {
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (ws.readyState === WebSocket.CONNECTING) {
        ws.onopen = () => ws.close();
      } else if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    }
  }, [stopTimers, cleanupMedia]);

  useEffect(() => {
    // Avoid starting prepare/reconnect during React StrictMode's disposable
    // test mount. Only the real mount's deferred initialization is allowed to
    // touch device state or open a socket.
    const timer = setTimeout(() => {
      if (!token) return;
      try {
        const raw = sessionStorage.getItem(auxSessionKey(token));
        if (raw) {
          const stored = JSON.parse(raw);
          if (stored.stoppedLocally) {
            if (isUrlMarkedStopped()) {
              // Reloading a stopped device keeps it stopped.
              setDeviceId(stored.deviceId || '');
              setSessionTitle(stored.sessionTitle || '');
              setPhase(PHASE.STOPPED_LOCAL);
              return;
            }
            // Opened again without the marker (QR code rescanned or link followed):
            // start over as a new device.
            sessionStorage.removeItem(auxSessionKey(token));
            connect();
            return;
          }
          reconnectFromStorage(stored);
          return;
        }
      } catch { /* ignore */ }
      if (isUrlMarkedStopped()) clearUrlStoppedMarker();
      connect();
    }, 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRecording = phase === PHASE.RECORDING;
  const isPaused = phase === PHASE.PAUSED;
  const isLive = isRecording || isPaused;
  const needsAwake = phase === PHASE.WAITING || isLive;

  // Hold the wake lock for as long as the device should be capturing.
  useEffect(() => {
    if (needsAwake) requestWakeLock();
    else releaseWakeLock();
  }, [needsAwake, requestWakeLock, releaseWakeLock]);

  // Browsers drop the lock when the page is hidden, so re-acquire on return.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && needsAwake) requestWakeLock();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [needsAwake, requestWakeLock]);

  useEffect(() => {
    const warnBeforeUnload = (e) => {
      if (!isLive) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', warnBeforeUnload);
    };
  }, [isLive]);

  const outerSx = {
    p: isMobile ? '24px 16px' : '32px 28px', minHeight: '100vh', backgroundColor: PAGE_BG,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center', fontFamily: FONT_FAMILY,
    boxSizing: 'border-box',
  };
  const contentSx = {
    width: '100%', maxWidth: 460, margin: '0 auto', textAlign: 'center',
  };
  const primaryBtnSx = {
    py: isMobile ? 2 : 1.6, fontSize: isMobile ? '15px' : '14px', fontWeight: 600,
    textTransform: 'none', borderRadius: '4px',
    backgroundColor: COLORS.primaryDark, boxShadow: 'none',
    '&:hover': BUTTON_HOVER_SX,
    '&.Mui-disabled': { backgroundColor: COLORS.disabled, color: COLORS.page },
  };
  const tryAgainBtnSx = {
    mt: 3, px: 2.5, py: 0.9, fontSize: '17px', fontWeight: 500,
    textTransform: 'none', borderRadius: '4px',
    backgroundColor: COLORS.primary, backgroundImage: BUTTON_HOVER.gradient, boxShadow: 'none',
    '&:hover': BUTTON_HOVER_SX,
  };

  const logo = (
    <Box
      component="img"
      src="/TTLogo.svg"
      alt="Talk Trees"
      sx={{ height: isMobile ? 34 : 40, width: 'auto', display: 'block', mx: 'auto' }}
    />
  );

  const header = (
    <>
      {logo}
      {sessionTitle && (
        <Box sx={{ mt: 7 }}>
          <Typography sx={{ fontFamily: FONT_HEADING, fontSize: '22px', color: COLORS.textMuted, lineHeight: 1.2 }}>
            Streaming live
          </Typography>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, color: COLORS.textMuted, mt: 0.5, wordBreak: 'break-word' }}>
            {sessionTitle}
          </Typography>
        </Box>
      )}
      {deviceId && (
        <Typography sx={{ fontSize: isMobile ? '40px' : '44px', fontWeight: 700, color: '#2D2D2D', mt: 7, lineHeight: 1.1 }}>
          {formatDeviceLabel(deviceId)}
        </Typography>
      )}
    </>
  );

  // No token in the URL: show the join-code entry screen.
  if (!token) {
    return (
      <ThemeProvider theme={appTheme}>
        <Box sx={outerSx}>
          <Box sx={contentSx}>
            {logo}
            <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#111827', mt: 5, mb: 0.5 }}>
              Enter join code
            </Typography>
            <Typography sx={{ fontSize: '14px', color: COLORS.textMuted, mb: 3 }}>
              Ask your teacher for the {JOIN_CODE_LENGTH}-character code shown on their screen.
            </Typography>

            {joinError && (
              <NoticeBox icon={<ErrorIcon sx={{ fontSize: 30 }} />} color={CORAL} bg={CORAL_BG}>
                {joinError}
              </NoticeBox>
            )}

            <TextField
              fullWidth
              variant="outlined"
              value={joinCodeInput}
              onChange={(e) => setJoinCodeInput(e.target.value.toUpperCase().slice(0, JOIN_CODE_LENGTH))}
              onKeyDown={(e) => { if (e.key === 'Enter' && !joinSubmitting) handleJoinWithCode(); }}
              placeholder="______"
              autoFocus
              inputProps={{
                maxLength: JOIN_CODE_LENGTH,
                style: { textAlign: 'center', fontSize: '28px', fontWeight: 700, letterSpacing: '0.35em', textTransform: 'uppercase' },
              }}
              sx={{ mt: 3, mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />

            <Button
              fullWidth variant="contained" onClick={handleJoinWithCode}
              disabled={joinCodeInput.trim().length !== JOIN_CODE_LENGTH || joinSubmitting}
              startIcon={joinSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
              sx={primaryBtnSx}
            >
              {joinSubmitting ? 'Joining…' : 'Join session'}
            </Button>
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={appTheme}>
      <Box sx={outerSx}>
        <Box sx={contentSx}>
          {header}

          {!wsConnected && (phase === PHASE.WAITING || phase === PHASE.RECORDING || phase === PHASE.PAUSED) && (
            <NoticeBox icon={<ErrorIcon sx={{ fontSize: 30 }} />} color={CORAL} bg={CORAL_BG}>
              Connection lost — reconnecting… Recording continues on this device either way.
            </NoticeBox>
          )}

          {needsAwake && (wakeLockStatus === 'unsupported' || wakeLockStatus === 'blocked') && (
            <NoticeBox icon={<InfoIcon sx={{ fontSize: 30, color: INFO_BLUE }} />} color={COLORS.text} bg="#FBFAF8">
              This device can't stay awake automatically. Keep the screen on, or turn off auto-lock, so recording isn't interrupted.
            </NoticeBox>
          )}

          {(phase === PHASE.IDLE || phase === PHASE.PREPARING) && (
            <Box sx={{ py: 6 }}>
              <CircularProgress size={34} sx={{ color: BLUE, mb: 2 }} />
              <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>
                Joining the session…
              </Typography>
            </Box>
          )}

          {phase === PHASE.REQUESTING && (
            <Box sx={{ py: 6 }}>
              <CircularProgress size={34} sx={{ color: BLUE, mb: 2 }} />
              <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#111827', mb: 1 }}>
                Requesting microphone access…
              </Typography>
              <Typography sx={{ fontSize: '13px', color: COLORS.textMuted, maxWidth: 320, mx: 'auto', lineHeight: 1.5 }}>
                Look for a permission prompt from your browser and choose <strong>Allow</strong>.
              </Typography>
            </Box>
          )}

          {phase === PHASE.WAITING && (
            <Box sx={{ py: 5 }}>
              <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: '#ecfdf5', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 36, color: GREEN }} />
              </Box>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                You're connected and ready
              </Typography>
              <Typography sx={{ fontSize: '14px', color: COLORS.textMuted }}>
                Recording will start automatically as soon as the teacher starts the session.
              </Typography>
            </Box>
          )}

          {phase === PHASE.DENIED && (
            <>
              <StatusPill bg={CORAL} icon={<MicOffOutlinedIcon sx={{ fontSize: 24 }} />} label="No mic" />
              <NoticeBox icon={<ErrorIcon sx={{ fontSize: 34 }} />} color={CORAL} bg={CORAL_BG}>
                {error}
              </NoticeBox>
              <Button variant="contained" onClick={connect} sx={tryAgainBtnSx}>
                Try again
              </Button>
            </>
          )}

          {phase === PHASE.ERROR && (
            <>
              <NoticeBox icon={<ErrorIcon sx={{ fontSize: 34 }} />} color={CORAL} bg={CORAL_BG}>
                {error}
              </NoticeBox>
              <Button variant="contained" onClick={connect} sx={tryAgainBtnSx}>
                Try again
              </Button>
            </>
          )}

          {isLive && (
            <>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mt: 3, color: isRecording ? GREEN : COLORS.textMuted }}>
                <SensorsIcon sx={{ fontSize: 46 }} />
                <Typography sx={{ fontSize: '30px', fontWeight: 800, color: 'inherit', lineHeight: 1 }}>
                  {isRecording ? 'Recording' : 'Paused'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: '6px', height: 64, mt: 3, mb: 3 }}>
                {bars.map((b, i) => (
                  <Box key={i} sx={{ width: 9, borderRadius: '6px', height: `${Math.max(8, b * 64)}px`, backgroundColor: isRecording ? BLUE : '#d1d5db', transition: 'height 0.08s ease-out' }} />
                ))}
              </Box>
              <Button
                variant="outlined" onClick={stopLocalDevice}
                sx={{
                  px: 2.5, py: 0.9, fontSize: '17px', fontWeight: 500, textTransform: 'none',
                  borderRadius: '6px', borderColor: '#8C8C8C', color: COLORS.text,
                  '&:hover': { borderColor: COLORS.text, backgroundColor: 'rgba(0,0,0,0.03)' },
                }}
              >
                Stop recording on this device
              </Button>
            </>
          )}

          {phase === PHASE.STOPPED_LOCAL && (
            <>
              <StatusPill bg={STOPPED_GRAY} icon={<MicOffOutlinedIcon sx={{ fontSize: 24 }} />} label="Recording stopped" />
              <NoticeBox icon={<InfoIcon sx={{ fontSize: 34, color: INFO_BLUE }} />} color={COLORS.text} bg="#FBFAF8">
                This session is still going for everyone else. To join back in, scan the QR code again from the teacher’s screen.
              </NoticeBox>
            </>
          )}

          {phase === PHASE.ENDED && (
            <Box sx={{ py: 4 }}>
              <Box sx={{ width: 88, height: 88, borderRadius: '50%', backgroundColor: '#ecfdf5', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 44, color: GREEN }} />
              </Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#111827', mb: 1 }}>
                Stream finished
              </Typography>
              <Typography sx={{ fontSize: '14px', color: COLORS.textMuted }}>
                Your recording has been sent. You can close this page.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AuxRecord;